import { ChainId, Currency, CurrencyAmount, Price, Token, TradeType } from '@uniswap/sdk-core'
import { useWeb3React } from '@web3-react/core'
import tryParseCurrencyAmount from 'lib/utils/tryParseCurrencyAmount'
import { useMemo, useRef } from 'react'
import { INTERNAL_ROUTER_PREFERENCE_PRICE } from 'state/routing/types'
import { useRoutingAPITrade } from 'state/routing/useRoutingAPITrade'

import { SupportedInterfaceChain, asSupportedChain } from 'constants/chains'
import { USDCe_BERACHAIN, USDCe_ZKSYNC } from '../constants/tokens'
import { SmartRouterTrade } from '../lib/SwapRouter'

// Stablecoin amounts used when calculating spot price for a given currency.
// The amount is large enough to filter low liquidity pairs.
export const STABLECOIN_AMOUNT_OUT: { [key in SupportedInterfaceChain]: CurrencyAmount<Token> } = {
  [ChainId.ZKSYNC]: CurrencyAmount.fromRawAmount(USDCe_ZKSYNC, 10_000e6),
  [ChainId.ZKSYNC_SEPOLIA_TESNET]: CurrencyAmount.fromRawAmount(USDCe_ZKSYNC, 10_000e6),
  [ChainId.BERACHAIN_CARTIO]: CurrencyAmount.fromRawAmount(USDCe_ZKSYNC, 10_000e6),
  // [ChainId.BERACHAIN_BARTIO]: CurrencyAmount.fromRawAmount(tUSDC_BERACHAIN_BARTIO, 10_000e6),
  [ChainId.BERACHAIN]: CurrencyAmount.fromRawAmount(USDCe_BERACHAIN, 10_000e6),
}

/**
 * Returns the price in USDC of the input currency
 * @param currency currency to compute the USDC price of
 */
export default function useStablecoinPrice(currency?: Currency): Price<Currency, Token> | undefined {
  const chainId = useMemo(() => asSupportedChain(currency?.chainId), [currency?.chainId])
  const amountOut = useMemo(() => (chainId ? STABLECOIN_AMOUNT_OUT[chainId] : undefined), [chainId])
  const stablecoin = amountOut?.currency
  const amountIn = useMemo(() => tryParseCurrencyAmount('1', currency), [currency])

  const { trade } = useRoutingAPITrade(
    false /* skip */,
    TradeType.EXACT_INPUT,
    amountIn,
    stablecoin,
    INTERNAL_ROUTER_PREFERENCE_PRICE
  )
  const price = useMemo(() => {
    if (!currency || !stablecoin) {
      return undefined
    }

    // handle usdc
    if (currency?.wrapped.equals(stablecoin)) {
      return new Price(stablecoin, stablecoin, '1', '1')
    }

    // if initial quoting fails, we may end up with a DutchOrderTrade
    if (trade && (trade as unknown as SmartRouterTrade)?.executionPrice) {
      return trade.executionPrice as Price<Currency, Token>
    }

    // // if initial quoting fails, we may end up with a DutchOrderTrade
    // if (trade && trade instanceof ClassicTrade) {
    //   const { numerator, denominator } = trade.routes[0].midPrice
    //   return new Price(currency, stablecoin, denominator, numerator)
    // }

    return undefined
  }, [currency, stablecoin, trade])

  const lastPrice = useRef(price)
  if (
    !price ||
    !lastPrice.current ||
    !price.equalTo(lastPrice.current) ||
    !price.baseCurrency.equals(lastPrice.current.baseCurrency)
  ) {
    lastPrice.current = price
  }
  return lastPrice.current
}

export function useStablecoinValue(currencyAmount: CurrencyAmount<Currency> | undefined | null) {
  const price = useStablecoinPrice(currencyAmount?.currency)

  return useMemo(() => {
    if (!price || !currencyAmount) return null
    try {
      return price.quote(currencyAmount)
    } catch (error) {
      return null
    }
  }, [currencyAmount, price])
}

/**
 *
 * @param fiatValue string representation of a USD amount
 * @returns CurrencyAmount where currency is stablecoin on active chain
 */
export function useStablecoinAmountFromFiatValue(fiatValue: number | null | undefined) {
  const { chainId } = useWeb3React()
  const supportedChainId = asSupportedChain(chainId)
  const stablecoin = supportedChainId ? STABLECOIN_AMOUNT_OUT[supportedChainId].currency : undefined

  return useMemo(() => {
    if (fiatValue === null || fiatValue === undefined || !chainId || !stablecoin) {
      return undefined
    }

    // trim for decimal precision when parsing
    const parsedForDecimals = fiatValue.toFixed(stablecoin.decimals).toString()
    try {
      // parse USD string into CurrencyAmount based on stablecoin decimals
      return tryParseCurrencyAmount(parsedForDecimals, stablecoin)
    } catch (error) {
      return undefined
    }
  }, [chainId, fiatValue, stablecoin])
}
